import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AnalyticsApi } from '@portal/pkg-api/dashboard'
import { data } from '@portal/pkg-api/shared'
import { ZonedDateTime } from '@js-joda/core'
import { EtlJob as EtlJobGen, EtlJobStatus } from '@portal/pkg-api/gen/analytics'

const prefix = 'dashboard.analytics'

export const AnalyticQueries = {
  etlStatus: (jobId?: number) => ({
    queryKey: [`${prefix}.etlStatus`, jobId],
    queryFn: () => data(AnalyticsApi.getEtlStatus(jobId!)),
    enabled: !!jobId,
  }),
  etlJob: (jobId?: number) => ({
    queryKey: [`${prefix}.etlJob`, jobId],
    queryFn: () => data(AnalyticsApi.getEtlJob(jobId!)),
    enabled: !!jobId,
    refetchInterval: 1000 * 30, // 30 seconds
  }),
  etlJobs: () => ({
    queryKey: [`${prefix}.etlJobs`],
    queryFn: async (): Promise<ReadonlyArray<EtlJob>> => {
      const response = await data(AnalyticsApi.getEtlJobs())
      return response.map(convertEtlJob)
    },
    refetchInterval: 1000 * 30, // 30 seconds
  }),
}

export interface EtlJob {
  id: number
  name?: string
  status: 'RUNNING' | 'QUEUED' | 'READY' | 'ERROR'
  lastWrittenDate?: ZonedDateTime
  disabled: boolean
}

export function useEtlStatus(jobId?: number) {
  return useQuery(AnalyticQueries.etlStatus(jobId))
}

export function useEtlJob(jobId?: number) {
  return useQuery(AnalyticQueries.etlJob(jobId))
}

export function useEtlJobs() {
  return useQuery(AnalyticQueries.etlJobs())
}

export function useClearError() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobId: number) =>
      AnalyticsApi.setEtlStatus({ [jobId.toString()]: EtlJobStatus.READY }),
    onSuccess: (_data, jobId) => {
      void queryClient.invalidateQueries(AnalyticQueries.etlJobs())
      void queryClient.invalidateQueries(AnalyticQueries.etlJob(jobId))
      void queryClient.invalidateQueries(AnalyticQueries.etlStatus(jobId))
    },
  })
}

export function useStartAnalyticsJobs() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobIds: Array<number>) => AnalyticsApi.startAnalyticsJobs(jobIds),
    onSuccess: (_data, jobIds) => {
      void queryClient.invalidateQueries(AnalyticQueries.etlJobs())
      jobIds.forEach((jobId) => {
        void queryClient.invalidateQueries(AnalyticQueries.etlJob(jobId))
        void queryClient.invalidateQueries(AnalyticQueries.etlStatus(jobId))
      })
    },
  })
}

const convertEtlJob = ({ lastWrittenDate, ...job }: EtlJobGen): EtlJob => ({
  ...job,
  lastWrittenDate: lastWrittenDate ? ZonedDateTime.parse(lastWrittenDate) : undefined,
  disabled: job.status !== EtlJobStatus.READY,
})
